import { formatDate, formatMoney, t } from "@hakili/i18n";
import {
  ActionBar,
  BANK_BORDER,
  BANK_FS_BODY,
  BANK_FS_META,
  BANK_FS_READ,
  BANK_TEXT,
  BANK_TEXT_2,
  Button,
  Callout,
  GuardedButton,
  Input,
  ProgressBar,
  Table,
} from "@hakili/ui";
import { Check, Info, Send } from "lucide-react";
import { type ReactElement, useState } from "react";

import { D2Card, GUARD_CHIP, GUARD_FOOT } from "./d2-parts.js";
import { DASH, fill } from "./dossiers.js";
import type { FundsMoment } from "./funds-moment.js";
import { type LaterObligationRow, ObligationsCard } from "./obligations-card.js";
import { ParcoursCard, type ParcoursDates } from "./parcours-card.js";

/**
 * `round T-2`, `M5` (`P2-BKW-005`'s ordre half, `P2-BKW-006`; WF-BNK-16 steps 5 and 6, WF-BNK-17;
 * `DECISION ADDED` 19): **the ordre in two acts on the bank's screen, and the décaissé state.**
 *
 * `GET /api/v1/bank/files/{id}/disbursement` is the card's one read: the two beneficiaries as the
 * record places them (the dealer's own account, entered by his responsable; Hakili's, published by
 * the administration), the two amounts on the SALE price at the frozen rate, the standing
 * settlement with its ordre and its funds. « Ordonner les deux virements » is live when nothing
 * blocks it and greyed with the door's own reason (a condition standing, a beneficiary's account
 * missing , WF-BNK-16's three sentences, I19); once ordered, the card reads the ordre's reference
 * with its two sub-references and offers « Fonds versés » with the two transfers' references
 * (WF-BNK-17); once the funds are recorded the file is `decaisse` and `DecaissePanel` reads the
 * payment card , one row per transfer, never one line for the full price.
 */
export interface BankAccountRead {
  readonly bankName: string;
  readonly holderName: string;
  readonly accountNumber: string;
}

export interface OrdreSettlementRead {
  readonly id: string;
  readonly reference: string;
  readonly status: string;
  readonly hakiliAmount: number;
  readonly dealerAmount: number;
  readonly hakiliLegConfirmedAt: string | null;
  readonly dealerLegConfirmedAt: string | null;
  readonly disbursement: {
    readonly orderReference: string;
    readonly dealerTransferReference: string | null;
    readonly hakiliTransferReference: string | null;
    readonly orderedAt: string;
    readonly disbursedAt: string | null;
    readonly dealerTransferRef: string | null;
    readonly hakiliTransferRef: string | null;
  } | null;
  readonly voucher: { readonly reference: string } | null;
}

export interface OrdreRead {
  readonly orderReference: string;
  /** The references the door quotes (« <ref>-V » / « <ref>-H », 05 §2.8.9), as served: `null` when the read has none, never built here. */
  readonly dealerTransferReference: string | null;
  readonly hakiliTransferReference: string | null;
  readonly salePriceAmount: number | null;
  readonly dealerAmount: number | null;
  readonly hakiliAmount: number | null;
  readonly ratePct: number | null;
  /** `ODF-2`: the price's two shares, said by the server (`dealerSharePct` + `hakiliSharePct`); the screen divides nothing. */
  readonly dealerSharePct: number | null;
  readonly hakiliSharePct: number | null;
  readonly beneficiaries: {
    readonly concessionnaire: BankAccountRead | null;
    readonly hakili: {
      readonly bankName: string;
      readonly accountName: string;
      readonly accountNumber: string;
    } | null;
  };
  readonly conditionsRestantes: readonly string[];
  /** `ODF-3`: when the last suspensive condition was lifted, the server's own field; `undefined` when the read does not deliver it. */
  readonly conditionsLiftedAt: string | null | undefined;
  readonly blockedReason: string | null;
  readonly settlement: OrdreSettlementRead | null;
}

/** A date in a table never breaks inside itself: no-break spaces at the joins (FRENCH-SPACING; the shared formatter is untouched). */
const bound = (text: string): string => text.replace(/ /gu, "\u00A0");

/** A share as the French screen says it: the server's figure with a decimal comma, no trailing noise. */
export const sharePlain = (share: number): string => String(share).replace(".", ",");
/** « 92 % », or the dash when the read carries no share (never a guess from the rate). */
export const sharePct = (share: number | null): string =>
  share === null ? DASH : fill("bank.decision.pct", { n: sharePlain(share) });

const str = (value: unknown): string | null => (typeof value === "string" ? value : null);
const num = (value: unknown): number | null => (typeof value === "number" ? value : null);
const rec = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;

function accountFrom(raw: unknown): BankAccountRead | null {
  const value = rec(raw);
  const bankName = str(value?.["bankName"]);
  const holderName = str(value?.["holderName"]);
  const accountNumber = str(value?.["accountNumber"]);
  return bankName === null || holderName === null || accountNumber === null
    ? null
    : { bankName, holderName, accountNumber };
}

function settlementFrom(raw: unknown): OrdreSettlementRead | null {
  const value = rec(raw);
  if (value === null) return null;
  const id = str(value["id"]);
  const reference = str(value["reference"]);
  const status = str(value["status"]);
  const hakiliAmount = num(value["hakiliAmount"]);
  const dealerAmount = num(value["dealerAmount"]);
  if (id === null || reference === null || status === null || hakiliAmount === null || dealerAmount === null)
    return null;
  const disbursement = rec(value["disbursement"]);
  const voucher = rec(value["voucher"]);
  const voucherReference = str(voucher?.["reference"]);
  return {
    id,
    reference,
    status,
    hakiliAmount,
    dealerAmount,
    hakiliLegConfirmedAt: str(value["hakiliLegConfirmedAt"]),
    dealerLegConfirmedAt: str(value["dealerLegConfirmedAt"]),
    disbursement:
      disbursement === null
        ? null
        : {
            orderReference: str(disbursement["orderReference"]) ?? "",
            dealerTransferReference: str(disbursement["dealerTransferReference"]),
            hakiliTransferReference: str(disbursement["hakiliTransferReference"]),
            orderedAt: str(disbursement["orderedAt"]) ?? "",
            disbursedAt: str(disbursement["disbursedAt"]),
            dealerTransferRef: str(disbursement["dealerTransferRef"]),
            hakiliTransferRef: str(disbursement["hakiliTransferRef"]),
          },
    voucher: voucherReference === null ? null : { reference: voucherReference },
  };
}

export function ordreFrom(answer: unknown): OrdreRead | null {
  const value = rec(answer);
  if (value === null) return null;
  const orderReference = str(value["orderReference"]);
  if (orderReference === null) return null;
  const beneficiaries = rec(value["beneficiaries"]);
  const hakili = rec(beneficiaries?.["hakili"]);
  const hakiliBank = str(hakili?.["bankName"]);
  const hakiliName = str(hakili?.["accountName"]);
  const hakiliNumber = str(hakili?.["accountNumber"]);
  const remaining = Array.isArray(value["conditionsRestantes"])
    ? (value["conditionsRestantes"] as unknown[]).filter((one): one is string => typeof one === "string")
    : [];
  return {
    orderReference,
    dealerTransferReference: str(value["dealerTransferReference"]),
    hakiliTransferReference: str(value["hakiliTransferReference"]),
    salePriceAmount: num(value["salePriceAmount"]),
    dealerAmount: num(value["dealerAmount"]),
    hakiliAmount: num(value["hakiliAmount"]),
    ratePct: num(value["ratePct"]),
    dealerSharePct: num(value["dealerSharePct"]),
    hakiliSharePct: num(value["hakiliSharePct"]),
    beneficiaries: {
      concessionnaire: accountFrom(beneficiaries?.["concessionnaire"]),
      hakili:
        hakiliBank === null || hakiliName === null || hakiliNumber === null
          ? null
          : { bankName: hakiliBank, accountName: hakiliName, accountNumber: hakiliNumber },
    },
    conditionsRestantes: remaining,
    conditionsLiftedAt: "conditionsLiftedAt" in value ? str(value["conditionsLiftedAt"]) : undefined,
    blockedReason: str(value["blockedReason"]),
    settlement: settlementFrom(value["settlement"]),
  };
}

/** D2's masked account: the first two letters and the last four figures, the rest as drawn (« ML•• •••• •• 4412 »). */
const maskAccount = (number: string): string =>
  number.length <= 6 ? DASH : `${number.slice(0, 2)}•• •••• •• ${number.slice(-4)}`;

/** D2: a day without its year (« 22 octobre »). */
const dayMonth = (iso: string): string => formatDate(new Date(iso)).replace(/ \d{4}$/u, "");

function TransferCard(props: {
  readonly testId: string;
  readonly n: number;
  readonly share: number | null;
  readonly amount: number | null;
  readonly who: string;
  readonly account: string | null;
  readonly against: string;
  readonly reference: string;
}): ReactElement {
  const row = (label: string, value: string, mono: boolean): ReactElement => (
    <div className={`flex justify-between gap-3 ${BANK_FS_BODY} ${BANK_TEXT_2}`} data-row="">
      <span>{label}</span>
      <span className={mono ? "[font-family:var(--hk-mono)] [font-variant-numeric:tabular-nums]" : ""}>
        {value}
      </span>
    </div>
  );
  return (
    <div
      className={`grid gap-2 rounded-[var(--hk-r-lg)] border bg-white px-5 py-[18px] ${BANK_BORDER}`}
      data-testid={props.testId}
    >
      <span
        className={`whitespace-nowrap text-[length:var(--hk-fs-meta)] font-semibold uppercase tracking-[0.1em] ${BANK_TEXT_2}`}
        data-meta=""
        data-share=""
      >
        {fill("bank.ordre.virementN", { n: String(props.n), taux: sharePct(props.share) })}
      </span>
      <div
        className={`text-[30px] font-semibold leading-[1.1] tracking-[-0.02em] [font-family:var(--hk-mono)] ${BANK_TEXT}`}
        data-amount=""
      >
        {props.amount === null ? DASH : formatMoney(props.amount).replace(/\s?FCFA$/u, "")}
        {props.amount === null ? null : (
          <span className={`text-base font-semibold ${BANK_TEXT_2}`}> FCFA</span>
        )}
      </div>
      <p className={`font-semibold ${BANK_FS_READ} ${BANK_TEXT}`} data-who="">
        {props.who}
      </p>
      {row(t("bank.ordre.compte"), props.account === null ? DASH : maskAccount(props.account), true)}
      {row(t("bank.ordre.contre"), props.against, false)}
      {row(t("bank.ordre.reference"), props.reference, true)}
    </div>
  );
}

/**
 * DESIGN SPRINT D-1026-2 (Lane E), D2 frames 47 and 48: **the ordre**. The callout (the price's two shares, from the server), the two
 * transfer cards (the beneficiary's account masked as drawn, the reference the door gives), the split, the totals sentence with the
 * two fees that enter no transfer, « Fonds versés » once the order is sent; the foot holds the act, « Revenir aux conditions » beside
 * « Ordonner ». The funds' three moments (ODF-1) replace the form and the foot's act.
 */
export function OrdrePanel(props: {
  readonly ordre: OrdreRead | null;
  /** The client's two file fees (the conditions' read), said by the totals sentence; `null` leaves that sentence out. */
  readonly fees: { readonly bankAmount: number; readonly hakiliAmount: number } | null;
  readonly busy: boolean;
  readonly onOrder: () => void;
  readonly onFunds: (dealerTransferReference: string, hakiliTransferReference: string) => void;
  /** `ODF-1`: the funds act's moment (the designer's three states); `idle` where nothing is happening. */
  readonly fundsMoment?: FundsMoment;
  readonly onReload?: () => void;
  /** Back to the conditions (frame 47's « Revenir aux conditions »). */
  readonly onBack: () => void;
}): ReactElement {
  const { ordre } = props;
  const fundsMoment = props.fundsMoment ?? "idle";
  const [refV, setRefV] = useState("");
  const [refH, setRefH] = useState("");
  const settlement = ordre?.settlement ?? null;
  const disbursement = settlement?.disbursement ?? null;
  const ordered = settlement !== null && disbursement !== null;
  const moment = ordered && (fundsMoment === "waiting" || fundsMoment === "late");
  const dealerAmount = ordre?.dealerAmount ?? null;
  const hakiliAmount = ordre?.hakiliAmount ?? null;
  const total = dealerAmount === null || hakiliAmount === null ? null : dealerAmount + hakiliAmount;
  const dealerAccount = ordre?.beneficiaries.concessionnaire ?? null;
  const hakiliAccount = ordre?.beneficiaries.hakili ?? null;
  const orderButton = (
    <>
      {ordre?.blockedReason !== null ? (
        <GuardedButton
          disabled
          {...GUARD_CHIP}
          reason={ordre === null ? t("bank.ordre.lectureMotif") : ordre.blockedReason}
          variant="primary"
          className={GUARD_FOOT}
          testId="bank-conditions-ordonner"
        >
          <span className="inline-flex items-center gap-2">
            <Send className="h-4 w-4" aria-hidden="true" />
            {t("bank.conditions.ordonner")}
          </span>
        </GuardedButton>
      ) : (
        <Button
          className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap px-[22px] text-[length:var(--hk-fs-read)]"
          disabled={props.busy}
          onClick={props.onOrder}
          data-testid="bank-conditions-ordonner"
        >
          <Send className="h-4 w-4" aria-hidden="true" />
          {t("bank.conditions.ordonner")}
        </Button>
      )}
      <Button variant="ghost" className="ml-auto" onClick={props.onBack} data-testid="bank-conditions-retour">
        {t("bank.conditions.retourConditions")}
      </Button>
    </>
  );
  const fundsButton =
    refV.trim() === "" || refH.trim() === "" ? (
      <GuardedButton
        disabled
        {...GUARD_CHIP}
        reason={t("bank.ordre.fondsMotif")}
        variant="primary"
        className={GUARD_FOOT}
        testId="bank-ordre-fonds"
      >
        <span className="inline-flex items-center gap-2">
          <Check className="h-4 w-4" aria-hidden="true" />
          {t("bank.ordre.fondsVerses")}
        </span>
      </GuardedButton>
    ) : (
      <Button
        className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap px-[22px] text-[length:var(--hk-fs-read)]"
        disabled={props.busy || fundsMoment === "saving"}
        data-testid="bank-ordre-fonds"
        onClick={() => {
          props.onFunds(refV.trim(), refH.trim());
        }}
      >
        <Check className="h-4 w-4" aria-hidden="true" />
        {fundsMoment === "saving" ? t("bank.ordre.fondsEnregistrement") : t("bank.ordre.fondsVerses")}
      </Button>
    );
  return (
    <div data-testid="bank-ordre-vue">
      <Callout
        tone="info"
        testId="bank-ordre-callout"
        className="mb-3"
        icon={
          <Info
            size={18}
            strokeWidth={2}
            className="mt-0.5 text-[color:var(--hk-brand-strong)]"
            aria-hidden="true"
          />
        }
      >
        <p>
          <b className="font-semibold">{t("bank.ordre.aucunVersement")}</b>{" "}
          {fill("bank.ordre.aucunVersementSuite", {
            dealer: sharePct(ordre?.dealerSharePct ?? null),
            hakili: sharePct(ordre?.hakiliSharePct ?? null),
          })}
        </p>
      </Callout>
      <div className="grid grid-cols-1 gap-4 min-[1100px]:grid-cols-2">
        <TransferCard
          testId="bank-ordre-virement-concessionnaire"
          n={1}
          share={ordre?.dealerSharePct ?? null}
          amount={dealerAmount}
          who={fill("bank.ordre.surSonCompte", {
            nom: dealerAccount === null ? t("bank.ordre.leConcessionnaire") : dealerAccount.holderName,
          })}
          account={dealerAccount === null ? null : dealerAccount.accountNumber}
          against={t("bank.ordre.factureProforma")}
          reference={ordre?.dealerTransferReference ?? DASH}
        />
        <TransferCard
          testId="bank-ordre-virement-hakili"
          n={2}
          share={ordre?.hakiliSharePct ?? null}
          amount={hakiliAmount}
          who={fill("bank.ordre.surSonCompte", { nom: t("bank.ordre.hakili") })}
          account={hakiliAccount === null ? null : hakiliAccount.accountNumber}
          against={t("bank.ordre.factureCommission")}
          reference={ordre?.hakiliTransferReference ?? DASH}
        />
      </div>
      <div className="mb-[18px] mt-3.5">
        {ordre?.dealerSharePct === undefined ||
        ordre.dealerSharePct === null ||
        ordre.hakiliSharePct === null ? null : (
          <div data-testid="bank-ordre-repartition">
            <ProgressBar
              label={fill("bank.ordre.repartition", {
                dealer: sharePlain(ordre.dealerSharePct),
                hakili: sharePlain(ordre.hakiliSharePct),
              })}
              parts={[
                { value: ordre.dealerSharePct, tone: "ink" },
                { value: ordre.hakiliSharePct, tone: "brand" },
              ]}
            />
          </div>
        )}
        {total === null ? null : (
          <p className={`mt-2 ${BANK_FS_BODY} ${BANK_TEXT_2}`} data-testid="bank-ordre-total">
            {t("bank.ordre.totalAvant")} <b className={`font-bold ${BANK_TEXT}`}>{formatMoney(total)}</b>
            {t("bank.ordre.totalApres")}
            {props.fees === null
              ? null
              : ` ${fill("bank.ordre.totalFrais", {
                  banque: formatMoney(props.fees.bankAmount).replace(/\s?FCFA$/u, ""),
                  hakili: formatMoney(props.fees.hakiliAmount).replace(/\s?FCFA$/u, ""),
                })}`}
          </p>
        )}
      </div>
      {ordered && moment ? (
        <div className="mb-4 w-full" role="status" data-testid="bank-ordre-fonds-moment">
          <Callout tone="info">
            <p className="text-sm">
              {fundsMoment === "waiting" ? (
                <>
                  {/* The joiner rule: the lead sentence at 600, the rest regular (the designer's review of ODF-1). */}
                  <span className="font-semibold" data-testid="bank-ordre-fonds-enregistres-lead">
                    {t("bank.ordre.fondsEnregistres")}
                  </span>{" "}
                  {t("bank.ordre.fondsEnregistresSuite")}
                </>
              ) : (
                t("bank.ordre.fondsEnRetard")
              )}
            </p>
            {fundsMoment === "late" ? (
              <Button
                variant="secondary"
                className="mt-2"
                onClick={() => {
                  props.onReload?.();
                }}
                data-testid="bank-ordre-recharger"
              >
                {t("bank.ordre.recharger")}
              </Button>
            ) : null}
          </Callout>
        </div>
      ) : null}
      {ordered && !moment ? (
        <D2Card title={t("bank.ordre.fondsVerses")} testId="bank-ordre">
          <p className={`mb-2.5 ${BANK_FS_BODY} ${BANK_TEXT}`} data-testid="bank-ordre-titre">
            {fill("bank.ordre.transmisD2", {
              reference: disbursement.orderReference,
              date: dayMonth(disbursement.orderedAt),
            })}
          </p>
          {disbursement.disbursedAt === null ? (
            <div
              className="grid grid-cols-1 gap-4 min-[1100px]:grid-cols-2"
              data-testid="bank-ordre-fonds-formulaire"
            >
              <div className="grid gap-1.5">
                <label
                  htmlFor="bank-ordre-ref-concessionnaire"
                  className={`${BANK_FS_BODY} font-semibold ${BANK_TEXT}`}
                >
                  {t("bank.ordre.referenceVirementConcessionnaire")}
                </label>
                <Input
                  id="bank-ordre-ref-concessionnaire"
                  value={refV}
                  onChange={setRefV}
                  className="[font-family:var(--hk-mono)]"
                  testId="bank-ordre-ref-concessionnaire"
                />
              </div>
              <div className="grid gap-1.5">
                <label
                  htmlFor="bank-ordre-ref-hakili"
                  className={`${BANK_FS_BODY} font-semibold ${BANK_TEXT}`}
                >
                  {t("bank.ordre.referenceVirementHakili")}
                </label>
                <Input
                  id="bank-ordre-ref-hakili"
                  value={refH}
                  onChange={setRefH}
                  className="[font-family:var(--hk-mono)]"
                  testId="bank-ordre-ref-hakili"
                />
              </div>
            </div>
          ) : (
            <p className={`${BANK_FS_BODY} ${BANK_TEXT_2}`} data-testid="bank-ordre-fonds-verses">
              {fill("bank.ordre.fondsVersesLe", { date: formatDate(new Date(disbursement.disbursedAt)) })}
            </p>
          )}
        </D2Card>
      ) : null}
      {moment || (ordered && disbursement.disbursedAt !== null) ? null : (
        <ActionBar label={t("bank.conditions.barreActions")}>{ordered ? fundsButton : orderButton}</ActionBar>
      )}
    </div>
  );
}

/** WF-BNK-17: the décaissé state , one row per transfer, the terminal line. `A-3` (frame `49`): a receipt and no button. */
interface TransferRow {
  readonly key: string;
  readonly subReference: string;
  readonly transferReference: string;
  readonly beneficiary: string;
  readonly against: string;
  readonly amount: number;
  readonly share: number | null;
  readonly valueDate: string | null;
}

export function DecaissePanel(props: {
  readonly ordre: OrdreRead | null;
  /** `ODF-2`: the file read's obligations after the disbursement, repeated here (D2 frame 49). */
  readonly laterObligations?: readonly LaterObligationRow[];
  /** `ODF-1`: the ordre's read is on its way (the file is read first, the ordre's own read after): the card says so, never a dash. */
  readonly loading?: boolean;
  /** `ODF-3`: the file's own typed dates for « Le parcours »; the lifted and disbursed dates come from the ordre's read. */
  readonly parcours?: Pick<ParcoursDates, "submittedAt" | "transmittedAt" | "contractSignedAt">;
}): ReactElement {
  const settlement = props.ordre?.settlement ?? null;
  const disbursement = settlement?.disbursement ?? null;
  const laterObligations = props.laterObligations ?? [];
  const dealerHolder = props.ordre?.beneficiaries.concessionnaire?.holderName ?? null;
  const rows: readonly TransferRow[] =
    settlement === null || disbursement === null
      ? []
      : [
          {
            key: "concessionnaire",
            subReference: disbursement.dealerTransferReference ?? DASH,
            transferReference: disbursement.dealerTransferRef ?? DASH,
            beneficiary: dealerHolder ?? t("bank.ordre.leConcessionnaire"),
            against: t("bank.ordre.factureProforma"),
            amount: settlement.dealerAmount,
            share: props.ordre?.dealerSharePct ?? null,
            valueDate: disbursement.disbursedAt,
          },
          {
            key: "hakili",
            subReference: disbursement.hakiliTransferReference ?? DASH,
            transferReference: disbursement.hakiliTransferRef ?? DASH,
            beneficiary: t("bank.ordre.hakili"),
            against: t("bank.ordre.factureCommission"),
            amount: settlement.hakiliAmount,
            share: props.ordre?.hakiliSharePct ?? null,
            valueDate: disbursement.disbursedAt,
          },
        ];
  const total = rows.reduce((sum, row) => sum + row.amount, 0);
  const money = (amount: number): string => formatMoney(amount).replace(/\s?FCFA$/u, "");
  return (
    <div data-testid="bank-decaisse">
      <Callout
        tone="ok"
        testId="bank-decaisse-callout"
        className="mb-3"
        icon={<Check size={18} strokeWidth={2} className="mt-0.5" aria-hidden="true" />}
      >
        <p>
          <b className="font-semibold">{t("bank.decaisse.executes")}</b> {t("bank.decaisse.suite")}
        </p>
      </Callout>
      <D2Card title={t("bank.decaisse.paiement")} testId="bank-decaisse-paiement">
        {rows.length === 0 ? (
          props.loading === true ? (
            <p className={`${BANK_FS_BODY} ${BANK_TEXT_2}`} data-testid="bank-decaisse-detail-arrive">
              {t("bank.decaisse.detailArrive")}
            </p>
          ) : (
            <p className={`${BANK_FS_BODY} ${BANK_TEXT_2}`}>{DASH}</p>
          )
        ) : (
          <div className="-mx-5 -mb-[18px] -mt-3" data-testid="bank-decaisse-virements">
            <Table
              flush
              columns={[
                {
                  key: "sub",
                  header: t("bank.decaisse.sousReference"),
                  cell: (row: TransferRow) => (
                    <>
                      <span
                        className={`block whitespace-nowrap ${BANK_FS_READ} [font-family:var(--hk-mono)] [font-variant-numeric:tabular-nums]`}
                        data-sub=""
                      >
                        {row.subReference}
                      </span>
                      <small
                        className={`block whitespace-nowrap ${BANK_FS_META} ${BANK_TEXT_2} [font-family:var(--hk-mono)]`}
                        data-transfer=""
                      >
                        {row.transferReference}
                      </small>
                    </>
                  ),
                },
                {
                  key: "beneficiary",
                  header: t("bank.conditions.beneficiaire"),
                  cell: (row) => <span className={BANK_FS_READ}>{row.beneficiary}</span>,
                },
                {
                  key: "share",
                  header: t("bank.decaisse.part"),
                  align: "right",
                  cell: (row) => (
                    <span className={`whitespace-nowrap ${BANK_FS_READ}`}>{sharePct(row.share)}</span>
                  ),
                },
                {
                  key: "amount",
                  header: t("bank.conditions.montant"),
                  align: "right",
                  cell: (row) => (
                    <span
                      className={`${BANK_FS_READ} [font-family:var(--hk-mono)] [font-variant-numeric:tabular-nums]`}
                    >
                      {money(row.amount)}
                    </span>
                  ),
                },
                {
                  key: "date",
                  header: t("bank.decaisse.dateValeur"),
                  cell: (row) => (
                    <span className={BANK_FS_READ}>
                      {row.valueDate === null ? DASH : bound(dayMonth(row.valueDate))}
                    </span>
                  ),
                },
                {
                  key: "against",
                  header: t("bank.decaisse.contre"),
                  cell: (row) => <span className={BANK_FS_READ}>{row.against}</span>,
                },
              ]}
              rows={rows}
              rowKey={(row) => row.key}
              footer={
                <tr>
                  <td colSpan={3}>
                    {/* D2 `tfoot td.muted`: the kit's `td` rule sets the cell, so the label carries its own size and ink. */}
                    <span className={`${BANK_FS_READ} ${BANK_TEXT_2}`}>{t("bank.decaisse.prixVente")}</span>
                  </td>
                  <td
                    className={`text-right font-bold ${BANK_FS_READ} [font-family:var(--hk-mono)] [font-variant-numeric:tabular-nums] ${BANK_TEXT}`}
                    data-total=""
                  >
                    {money(total)}
                  </td>
                  <td colSpan={2} />
                </tr>
              }
            />
          </div>
        )}
      </D2Card>
      {props.parcours === undefined || props.ordre === null ? (
        laterObligations.length === 0 ? null : (
          <ObligationsCard variant="decaisse" obligations={laterObligations} />
        )
      ) : (
        // D2 frame 49: « Le parcours » on the left, « Obligations après le décaissement » on the right; stacked below 1100 px.
        <div className="grid grid-cols-1 gap-4 min-[1100px]:grid-cols-2" data-testid="bank-decaisse-apres">
          <ParcoursCard
            dates={{
              ...props.parcours,
              conditionsLiftedAt: props.ordre.conditionsLiftedAt,
              disbursedAt: disbursement?.disbursedAt ?? null,
            }}
          />
          {laterObligations.length === 0 ? null : (
            <ObligationsCard variant="decaisse" obligations={laterObligations} />
          )}
        </div>
      )}
    </div>
  );
}
